import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { AngularFirestore, DocumentData } from '@angular/fire/compat/firestore';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ProjectsDataService } from 'src/app/services/projects-data.service';
import Swal from 'sweetalert2';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-hide-proyect',
  templateUrl: './hide-proyect.component.html',
  styleUrls: ['./hide-proyect.component.css'],
})
export class HideProyectComponent implements OnInit, OnDestroy {
  formSetHiden: FormGroup = this.createFormSetHiden();
  availableTokens: any[] = []
  
  private apiUrl = environment.api.url;
  private destroy$ = new Subject<void>();

  constructor(
    private afs: AngularFirestore,
    private http: HttpClient,
    private projectsDataService: ProjectsDataService,
    private cdr: ChangeDetectorRef
  ) {}

  showPercentageToPrivateSale: boolean = false;
  isLoadingTokens: boolean = false;

  ngOnInit() {
    this.loadTokens();
  }

  loadTokens() {
    this.isLoadingTokens = true;
    console.time('Service: Load Tokens');
    
    // Usar getAllProjects() para cargar TODOS los proyectos (incluidos ocultos)
    // Esto permite gestionar la visibilidad de proyectos ya ocultos
    this.projectsDataService.getAllProjects().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        console.timeEnd('Service: Load Tokens');
        const projects = Array.isArray(response) ? response : response.data || [];
        
        this.availableTokens = projects.map((p: any) => ({
          nameTokens: p.title || p.name || p.nameTokens,
          id: p.id || p._id
        }));
        
        this.isLoadingTokens = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading projects via Service', error);
        console.timeEnd('Service: Load Tokens');
        this.isLoadingTokens = false;
        this.cdr.detectChanges();
        
        // Fallback or retry could process here
        Swal.fire({
            icon: 'error',
            title: 'Error de carga',
            text: 'No se pudieron cargar los proyectos. Intentalo de nuevo.'
        });
      }
    });
  }

  // Llamado cuando el dropdown cambia
  onVisibilityChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const value = selectElement.value;
    this.showPercentageToPrivateSale = value === 'inversors';
  }
  createFormSetHiden() {
    return new FormGroup({
      // Usar '' en lugar de null para coincidir con los valores del HTML
      // Esto previene problemas de selección múltiple en los dropdowns
      nameToken: new FormControl('', [Validators.required]),
      hide: new FormControl('', [Validators.required]),
      projectVisibility: new FormControl('', [Validators.required]),
      percentageToPrivateSale: new FormControl(75, [ Validators.min(0), Validators.max(100)]),
    });
  }

  get percentageToPrivateSale() {
    return this.formSetHiden?.get('percentageToPrivateSale');
  }

  get nameToken() {
    return this.formSetHiden?.get('nameToken');
  }
  get hide() {
    return this.formSetHiden?.get('hide');
  }

  get projectVisibility() {
    return this.formSetHiden?.get('projectVisibility');
  }

  setHide() {
    if (this.formSetHiden.invalid) {
      this.formSetHiden.markAllAsTouched();
      return;
    }

    Swal.fire({
      title: 'Procesando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    // Optimización: Usar ID directamente (obtenido del dropdown)
    const id = this.nameToken?.value;

    if (!id) {
        Swal.close();
        Swal.fire({ icon: 'error', title: 'Error', text: 'ID de proyecto no válido' });
        return;
    }

    this.showPercentageToPrivateSale = false;
    // Preparar datos base para la actualización
    const updateData: any = {};
    
    // Manejar visibilidad del proyecto (ocultar/mostrar)
    const visibilityValue = this.projectVisibility?.getRawValue();
    if (visibilityValue === 'hidden') {
    updateData.hide = true;
    } else if (visibilityValue === 'visible') {
    updateData.hide = false;
    }
    
    // Manejar estado del proyecto
    const statusValue = this.hide?.getRawValue();
    switch (statusValue) {
    case 'keep-current': {
        // No cambiar el estado del proyecto, solo la visibilidad
        // No se agrega proyectStatus, managingInvestments, ni onlyInversors a updateData
        break;
    }
    case 'managing': {
        updateData.proyectStatus = 'Activo';
        updateData.managingInvestments = true;
        updateData.onlyInversors = false;
        break;
    }
    case 'false': {
        updateData.proyectStatus = 'Activo';
        updateData.onlyInversors = false;
        updateData.managingInvestments = false;
        break;
    }
    case 'inversors': {
        updateData.proyectStatus = 'Activo';
        updateData.onlyInversors = true;
        updateData.managingInvestments = false;
        updateData.blockchain = true;
        updateData.percentageToPrivateSale = this.percentageToPrivateSale?.value;
        break;
    }
    }
    
    // Aplicar cambios directamente al Doc por ID (Sin búsqueda)
    this.afs
    .collection('investments')
    .doc(id)
    .set(updateData, { merge: true })
    .then(() => {
        Swal.close();
        let message = '';
        if (statusValue === 'keep-current') {
        message = 'Visibilidad actualizada (estado del proyecto sin cambios)';
        } else if (statusValue === 'managing') {
        message = 'Proyecto en estado: Gestionando Reinversiones';
        } else if (statusValue === 'false') {
        message = 'Proyecto en suscripción general';
        } else if (statusValue === 'inversors') {
        message = 'Proyecto en pre-venta';
        }
        
        if (visibilityValue === 'hidden') {
        message += ' (Oculto)';
        } else if (visibilityValue === 'visible') {
        message += ' (Visible)';
        }
        
        Swal.fire({
        icon: 'success',
        text: message,
        });

        this.formSetHiden.reset();
    })
    .catch((error) => {
        console.error(error);
        Swal.close();
        Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Algo ha ido mal!',
        });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
